import { useCallback, useEffect, useMemo, useState } from "react";
import { devClubOverrideHeader, getAuthTokenForRequest } from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";

type FilmstripRequest = {
  matchId?: string | null;
  slot?: string | null;
  start: number;
  end: number;
  frames: number;
  enabled?: boolean;
};

export type TimelineFilmstrip = {
  url: string | null;
  status: "idle" | "loading" | "ready" | "error";
  retry: () => void;
};

const WINDOW_DEBOUNCE_MS = 200;
const REQUEST_TIMEOUT_MS = 52_000;
const RETRY_DELAY_MS = 1_000;
const MAX_RETRY_DELAY_MS = 10_000;

class PreviewResponseError extends Error {
  constructor(readonly transient: boolean, readonly retryAfter: string | null) {
    super("Timeline previews unavailable");
  }
}

/** Do not retry earlier than the server asks, or wait indefinitely in the UI. */
function retryDelay(error: unknown): number | null {
  if (!(error instanceof PreviewResponseError)) return RETRY_DELAY_MS;
  if (!error.transient) return null;
  if (!error.retryAfter) return RETRY_DELAY_MS;
  const seconds = Number(error.retryAfter);
  const requestedDelay = Number.isFinite(seconds)
    ? seconds * 1_000
    : Date.parse(error.retryAfter) - Date.now();
  const delay = Number.isFinite(requestedDelay)
    ? Math.max(RETRY_DELAY_MS, requestedDelay)
    : RETRY_DELAY_MS;
  return delay <= MAX_RETRY_DELAY_MS ? delay : null;
}

/**
 * Access-controlled JPEG sprites need raw fetch for blob responses and legacy
 * bearer auth. Keep credentials and the dev host override, but handle preview
 * failures locally: unavailable thumbnails must never fail the recording.
 *
 * Each window gets one debounced request and at most one transient retry. Both
 * fetching and reading the body share a deadline. A scope/actor change aborts
 * pending work and revokes its URL; the render key also hides old media before
 * the effect cleanup runs.
 */
export function useTimelineFilmstrip({
  matchId,
  slot,
  start,
  end,
  frames,
  enabled = true,
}: FilmstripRequest): TimelineFilmstrip {
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const clubId = useAuthStore((state) => state.currentClub?.id ?? null);
  const hydrating = useAuthStore((state) => state.hydrating);
  const teamId = useScopeStore((state) => state.teamId);
  const seasonId = useScopeStore((state) => state.seasonId);
  const endpoint = useMemo(() => {
    if (!enabled || !userId || hydrating || !matchId || !slot || end <= start) return null;
    const params = new URLSearchParams({
      slot,
      start: (Math.floor(start * 10) / 10).toFixed(1),
      end: (Math.ceil(end * 10) / 10).toFixed(1),
      frames: String(frames),
    });
    return `/api/matches/${encodeURIComponent(matchId)}/filmstrip?${params.toString()}`;
  }, [enabled, end, frames, hydrating, matchId, slot, start, userId]);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const key = JSON.stringify([endpoint, userId, clubId, teamId, seasonId, attempt]);
  const [preview, setPreview] = useState<{
    key: string;
    url: string | null;
    status: TimelineFilmstrip["status"];
  }>({ key: "", url: null, status: "idle" });

  useEffect(() => {
    setPreview({ key, url: null, status: endpoint ? "loading" : "idle" });
    if (!endpoint) return;
    let active = true;
    let objectUrl: string | null = null;
    let controller: AbortController | null = null;
    let deadline: ReturnType<typeof setTimeout> | undefined;
    let scheduled: ReturnType<typeof setTimeout> | undefined;

    const load = async (automaticRetry = false) => {
      controller = new AbortController();
      const requestController = controller;
      try {
        const token = getAuthTokenForRequest();
        const request = async () => {
          const response = await fetch(endpoint, {
            credentials: "include",
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...devClubOverrideHeader(),
            },
            signal: requestController.signal,
          });
          if (!response.ok) {
            throw new PreviewResponseError(
              response.status === 408 || response.status === 429 || response.status >= 500,
              response.headers.get("Retry-After"),
            );
          }
          const blob = await response.blob();
          // A proxy's successful HTML fallback is not a usable sprite. Keep
          // that failure retryable by the coach rather than showing "ready"
          // forever with a broken CSS background image.
          if (blob.size === 0 || blob.type.split(";")[0].toLowerCase() !== "image/jpeg") {
            throw new PreviewResponseError(false, null);
          }
          return blob;
        };
        const timeout = new Promise<never>((_, reject) => {
          deadline = setTimeout(() => {
            reject(new Error("Timeline preview request timed out"));
            requestController.abort();
          }, REQUEST_TIMEOUT_MS);
        });
        const blob = await Promise.race([request(), timeout]);
        if (!active || requestController.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setPreview({ key, url: objectUrl, status: "ready" });
      } catch (error) {
        if (!active) return;
        const delay = automaticRetry ? null : retryDelay(error);
        if (delay !== null) {
          scheduled = setTimeout(() => void load(true), delay);
        } else {
          setPreview({ key, url: null, status: "error" });
        }
      } finally {
        clearTimeout(deadline);
      }
    };

    scheduled = setTimeout(() => void load(), WINDOW_DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(scheduled);
      clearTimeout(deadline);
      controller?.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [endpoint, key]);

  if (!endpoint) return { url: null, status: "idle", retry };
  if (preview.key !== key) return { url: null, status: "loading", retry };
  return { url: preview.url, status: preview.status, retry };
}
